/**
 * The screen a scanned QR lands on.
 *
 * This runs on a phone that is not ours: borrowed, mid-evening, across a dim
 * table, with no account and no intention of getting one. So three things hold
 * here that hold nowhere else in the app.
 *
 * **Nothing durable is written to the device.** The grant goes into
 * `sessionStorage` through `saveGrant`, never `localStorage` and never the
 * store — a lent phone must not still be carrying a seat at someone else's
 * table next Tuesday. The screen says so in words wherever a grant exists,
 * because the person who owns the phone is standing right there.
 *
 * **The server decides, the screen reports.** Expiry is not judged against the
 * clock on a borrowed phone — a device an hour fast would lock its owner out of
 * a perfectly live code with no way past. The claim is attempted and the
 * server's answer is believed. Likewise a rejected claim re-reads the roster
 * rather than parsing prose: "the name went to another phone" and "the night is
 * over" arrive as the same status, and the roster is the thing that knows.
 *
 * **Every failure is a sentence, never a number.** Nobody at a table can act on
 * a 410.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { navigate } from "../../main.tsx";
import type { ScreenProps } from "../../main.tsx";
import { BoxArt } from "../catalog/components.tsx";
import type { ArtSubject } from "../catalog/components.tsx";
import {
  ApiError,
  claimSeat,
  clearGrant,
  fetchJoinRoster,
  grantVerdict,
  readGrant,
  saveGrant,
} from "./api.ts";
import type { Grant, GrantVerdict, JoinRoster } from "./api.ts";

/**
 * The claim response as this screen treats it: every field unknown until proven.
 *
 * `api.ts` declares the wire shape, but a captive portal or a stale service
 * worker can answer 200 with something else entirely, and the token is the one
 * field worth reading back — the session and the name are already known from the
 * roster and the tap.
 */
interface ClaimWire {
  token?: unknown;
  session_id?: unknown;
}

type Phase =
  | { kind: "loading" }
  // `reclaimed` is the seat picker arrived at the long way round: this phone was
  // holding a grant for this night, the server no longer honours it, and the
  // token has just been thrown away to get here. Worth saying out loud, because
  // from the guest's side nothing happened — they scanned the same code they
  // scanned an hour ago and are being asked to pick a name again.
  | { kind: "roster"; roster: JoinRoster; reclaimed: boolean }
  | { kind: "resume"; roster: JoinRoster; grant: Grant }
  | { kind: "expired" }
  // The session behind a finished night, when the roster named one. It is what
  // lets this screen offer the results back: a guest's grant outlives completion,
  // so the phone that helped produce the board can still open it.
  | { kind: "completed"; title: string | null; sessionId: string | null }
  | { kind: "missing" }
  | { kind: "offline" }
  | { kind: "broken" };

/** Everything this screen consults, injected so the decision can be stepped. */
export interface JoinDeps {
  roster?: (code: string) => Promise<JoinRoster>;
  held?: (sessionId?: string) => Grant | null;
  verdict?: (grant: Grant) => Promise<GrantVerdict>;
  forget?: () => void;
}

/**
 * What a roster read means, including what it means about the token this phone
 * already has.
 *
 * **The stored grant is no longer believed on sight.** It used to be: anything
 * `readGrant` returned won, and the screen said "you are in — back to rating". So
 * when the host took a guest's seat back, that guest's browser was finished for
 * the night. Its poll was refused, its reload was refused, and re-scanning the
 * code — the one thing anybody at a table does — landed here and was told it was
 * already in, back onto the same refusal. Nothing ever threw the token away, so
 * the fresh join could not get past the stale one. A private window worked all
 * evening, which is how the dead state was placed on the client.
 *
 * So a stored grant is now checked with the server before it is offered, and the
 * check is a real request carrying that token. This screen has said from the
 * beginning that the server decides and the screen reports — it does not judge
 * expiry against a borrowed phone's clock and it does not parse prose — and a
 * token is the same kind of thing: only the server knows whether one is still a
 * seat. Reading it off the roster instead was the tempting shortcut and it is
 * wrong twice over: `takeBackSeat` clears `claimed_at` so the seat does read as
 * open, but `reclaimAnswerlessSeat` deliberately keeps it (the host is sitting
 * there now), and neither column says anything about a grant that simply ran out.
 * A guess that covers one revocation path out of two is how this bug gets filed a
 * second time.
 *
 * Only `dead` clears. `unknown` — no route to the host, a captive portal, a 5xx —
 * keeps the grant and keeps offering it, because a phone in a basement is not a
 * phone that has lost its seat, and a check that destroyed a token on a bad
 * network would be the original lockout wearing a different hat.
 *
 * The finished-night branch returns before any of this, and that ordering is
 * load-bearing rather than incidental: after `9c13852` a guest's grant outlives
 * completion by a day, on purpose, so the phone that helped fill the board can
 * still read it. Verifying here would be harmless — a live grant answers `200` —
 * but the branch below is what offers the results back, and nothing about a night
 * that is over should be able to reach a line that throws a token away.
 */
export async function classifyJoin(roster: JoinRoster, deps: JoinDeps = {}): Promise<Phase> {
  const held = deps.held ?? readGrant;
  const verdict = deps.verdict ?? grantVerdict;
  const forget = deps.forget ?? clearGrant;

  // The body is an unchecked cast on the way in. A captive portal answering 200
  // with its own JSON would otherwise throw inside render, and React 19 unmounts
  // the tree on a render throw — a literally blank phone.
  if (typeof roster.session_id !== "string" || !Array.isArray(roster.participants)) {
    return { kind: "broken" };
  }

  // A finished night outranks everything: there is nothing left to join and no
  // name left to pick. It is not a dead end either — the session id travels with
  // it, so a phone still holding this night's grant is offered the results.
  if (roster.status === "COMPLETED") {
    return { kind: "completed", title: roster.game_title, sessionId: roster.session_id };
  }

  const grant = held(roster.session_id);
  if (!grant) return { kind: "roster", roster, reclaimed: false };
  if ((await verdict(grant)) === "dead") {
    forget();
    return { kind: "roster", roster, reclaimed: true };
  }

  return { kind: "resume", roster, grant };
}

/**
 * The whole of what this screen concludes from a scanned code, in one call.
 *
 * Exported and injectable because the interesting half of it is a sequence rather
 * than a branch: which refusals reach `classifyJoin` at all is exactly what
 * decides whether a token survives. An expired code and a finished night are both
 * answered before anything looks at a grant — the first because the roster read
 * throws and lands below, the second because `classifyJoin` returns on the status
 * first — and a test that only stepped the branches would not be able to say so.
 * The component is then a `setPhase` around this.
 */
export async function resolveJoin(code: string, deps: JoinDeps = {}): Promise<Phase> {
  const roster = deps.roster ?? fetchJoinRoster;
  try {
    return await classifyJoin(await roster(code), deps);
  } catch (err) {
    return classifyError(err);
  }
}

function classifyError(err: unknown): Phase {
  if (err instanceof ApiError) {
    if (err.status === 410) return { kind: "expired" };
    // A refused claim, which names no session: the roster read is what carries
    // one, and this path is the answer to a POST that failed.
    if (err.status === 409) return { kind: "completed", title: null, sessionId: null };
    // A code the server has never minted. A night that has just finished no
    // longer lands here — completion keeps its code precisely so the roster read
    // above can say "finished" instead — but a code from a deleted session, or
    // one somebody typed, still does.
    if (err.status === 404) return { kind: "missing" };
    return { kind: "broken" };
  }
  // fetch itself threw: no route to the host, captive portal, basement wifi.
  return { kind: "offline" };
}

function clockTime(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Said wherever this device is holding, or about to hold, a grant. */
function StorageNotice(): ReactNode {
  return (
    <div className="pb-notice">
      <span className="pb-notice__strong">On this phone</span>
      <span>
        Your place is kept in this browser tab only. Close the tab and the phone forgets the game —
        nothing is saved to it and no account is created.
      </span>
    </div>
  );
}

export function Join(props: ScreenProps): ReactNode {
  const code = props.params.code ?? "";

  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [chosen, setChosen] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [nameLost, setNameLost] = useState(false);
  // A second tap landing before React commits `claiming` would POST twice, and
  // the loser of that race is the person who just succeeded.
  const inFlight = useRef(false);

  useEffect(() => {
    let alive = true;
    setPhase({ kind: "loading" });
    void (async () => {
      // Fully resolved before anything is shown: `resolveJoin` may have a token
      // to check with the server, and flashing "you are in" at a phone that is
      // about to be sent back to the seat picker is the same lie in a shorter
      // form.
      const next = await resolveJoin(code);
      if (alive) setPhase(next);
    })();
    return () => {
      alive = false;
    };
  }, [code, attempt]);

  /** Re-read the roster. The selection never survives it — the seats may not have. */
  const reload = useCallback((opts: { keepNotice?: boolean } = {}) => {
    setChosen(null);
    if (!opts.keepNotice) setNameLost(false);
    setAttempt((n) => n + 1);
  }, []);

  const claim = useCallback(
    async (roster: JoinRoster, participantId: string) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setClaiming(true);
      setNameLost(false);
      try {
        const wire = (await claimSeat(code, participantId)) as unknown as ClaimWire;
        const token = typeof wire.token === "string" ? wire.token : "";
        if (!token) {
          setPhase({ kind: "broken" });
          return;
        }

        const sessionId = typeof wire.session_id === "string" ? wire.session_id : roster.session_id;
        saveGrant({ token, session_id: sessionId, participant_id: participantId });
        // Guest mode from here: the Rating screen reads this grant back and
        // writes over HTTP. The outbox and sync belong to the owner's device.
        navigate(`/session/${sessionId}`);
      } catch (err) {
        if (err instanceof ApiError && (err.status === 409 || err.status === 404)) {
          // The name went to another phone, or it is no longer in this session.
          // Re-read rather than guess which, and put them back in front of the
          // names that are still open.
          setNameLost(true);
          reload({ keepNotice: true });
          return;
        }
        setPhase(classifyError(err));
      } finally {
        inFlight.current = false;
        setClaiming(false);
      }
    },
    [code, reload],
  );

  if (phase.kind === "loading") {
    return (
      <Shell sub="Join">
        <div className="pb-notice" role="status">
          <span className="pb-notice__strong">Loading</span>
          <span>Looking up the game.</span>
        </div>
      </Shell>
    );
  }

  if (phase.kind === "resume") {
    const { roster, grant } = phase;
    const seat = roster.participants.find((p) => p.id === grant.participant_id);
    return (
      <Shell title={roster.game_title} art={rosterArt(roster)} sub="Join">
        <div className="pb-notice" data-tone="ok" role="status">
          <span className="pb-notice__strong">You are in</span>
          <span>
            {seat
              ? `This phone is already rating as ${seat.name}.`
              : "This phone is already rating for someone at this table."}
          </span>
        </div>
        <StorageNotice />
        <div className="pb-actions">
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            onClick={() => navigate(`/session/${roster.session_id}`)}
          >
            Back to rating
          </button>
        </div>
      </Shell>
    );
  }

  if (phase.kind === "roster") {
    const { roster } = phase;
    // A name that went while this screen was open must not stay pickable.
    const chosenSeat = roster.participants.find((p) => p.id === chosen && !p.claimed) ?? null;
    const open = roster.participants.filter((p) => !p.claimed).length;

    return (
      <Shell title={roster.game_title} art={rosterArt(roster)} sub="Join">
        <p className="pb-hint">
          Tap your name to join the review. What you rate stays hidden from the others until
          everyone at the table has answered.
        </p>

        {/* Said before the names, because it is the answer to the question the
            person holding the phone is actually asking: they scanned the same
            code as before and are being asked to pick again. Not an alert — no
            rating was lost that the host had not already been warned they were
            throwing away, and the way forward is the screen they are on. */}
        {phase.reclaimed ? (
          <div className="pb-notice" data-tone="wait" role="status">
            <span className="pb-notice__strong">Pick again</span>
            <span>
              This phone&rsquo;s place at the table was given back, so it is not rating any more.
              Choose a name below to join again.
            </span>
          </div>
        ) : null}

        {nameLost ? (
          <div className="pb-notice" data-tone="alert" role="status">
            <span className="pb-notice__strong">Gone</span>
            <span>That name is not available any more. Pick another one.</span>
          </div>
        ) : null}

        <div className="pb-section">
          <div className="pb-label">Who are you?</div>
          <div className="pb-roster">
            {roster.participants.map((p) => (
              <button
                key={p.id}
                type="button"
                className="pb-chip"
                data-on={chosen === p.id ? "true" : "false"}
                aria-pressed={chosen === p.id}
                disabled={p.claimed || claiming}
                onClick={() => setChosen(p.id)}
              >
                <span className="pb-swatch" style={{ background: p.colour }} />
                <span className="pb-seat__name">{p.name}</span>
                <span className="pb-seat__state" data-state={p.claimed ? "in" : "waiting"}>
                  {p.claimed ? "Taken" : "Open"}
                </span>
              </button>
            ))}
          </div>
          {open === 0 ? (
            <div className="pb-notice" data-tone="wait" role="status">
              <span className="pb-notice__strong">Full</span>
              <span>Every name is taken. Ask whoever set the game up to add you.</span>
            </div>
          ) : null}
        </div>

        <StorageNotice />

        {/* Confirm, rather than claim on the first tap: a claim cannot be undone
            from this screen, and a mis-tap in poor light takes a stranger's name. */}
        <div className="pb-actions">
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            disabled={!chosenSeat || claiming}
            onClick={() => {
              if (chosenSeat) void claim(roster, chosenSeat.id);
            }}
          >
            {claiming
              ? "Joining"
              : chosenSeat
                ? `Continue as ${chosenSeat.name}`
                : "Pick your name"}
          </button>
        </div>

        {roster.expires_at !== null ? (
          <div className="pb-code__hint">Joining closes at {clockTime(roster.expires_at)}</div>
        ) : null}
      </Shell>
    );
  }

  return <Failure phase={phase} code={code} onRetry={() => reload()} />;
}

// ---------------------------------------------------------------------------
// Dead ends
// ---------------------------------------------------------------------------

function Failure(props: { phase: Phase; code: string; onRetry: () => void }): ReactNode {
  const { phase, code, onRetry } = props;

  // Where a phone that already joined tonight can still go.
  //
  // On a finished night this is the whole point of the screen rather than a
  // consolation. A guest's grant outlives completion — it stops being a licence
  // to write and stays a handle on the revealed board — so the phone that helped
  // produce those results is one tap from reading them, and the roster told us
  // which night they belong to. This is the exit production did not have: every
  // route back was a 410 or a 404, on the one artefact the evening exists for.
  //
  // Unscoped everywhere else, because those phases are a roster read that failed
  // and there is no session to match against; whatever grant this phone holds is
  // still better than a dead end.
  const finished = phase.kind === "completed" ? phase.sessionId : null;
  const stray = finished !== null ? readGrant(finished) : readGrant();
  const strayLabel =
    finished !== null && stray ? "See the results" : "Back to the game you joined";

  let tone: "alert" | "wait" = "alert";
  let strong = "Problem";
  let headline = "Something went wrong at our end.";
  let detail = "Nothing you did caused this. Try again in a moment.";
  let showCode = false;

  if (phase.kind === "expired") {
    tone = "wait";
    strong = "Expired";
    headline = "This code has run out.";
    detail = "Ask whoever set the game up to show the QR code again — it only takes a second.";
  } else if (phase.kind === "completed") {
    tone = "wait";
    strong = "Finished";
    headline = phase.title
      ? `The review of ${phase.title} is already done.`
      : "This game night is already wrapped up.";
    detail = stray
      ? "There is nothing left to rate — but the board this phone helped fill in is still here."
      : "There is nothing left to rate here.";
  } else if (phase.kind === "missing") {
    strong = "No game";
    headline = "That link did not open a game.";
    detail =
      "The game may have finished, or whoever set it up may have closed joining. Ask them to show the QR code again.";
    showCode = true;
  } else if (phase.kind === "offline") {
    strong = "Offline";
    headline = "This phone cannot reach the game.";
    // Scoped to this screen on purpose. The blanket version was read by anyone
    // who arrived here from a rating they had not managed to send, and it told
    // them their number was safe at the exact moment it was not. This screen can
    // only vouch for itself.
    detail = "Check the wifi and try again. Nothing you did on this screen is lost.";
  }

  return (
    <Shell sub="Join">
      <div className="pb-join">
        <div className="pb-notice" data-tone={tone} role="alert">
          <span className="pb-notice__strong">{strong}</span>
          <span>{headline}</span>
        </div>
        <p className="pb-hint">{detail}</p>
        {showCode && code ? (
          <>
            <div className="pb-code">{code}</div>
            <div className="pb-code__hint">The code this phone tried</div>
          </>
        ) : null}
      </div>

      {phase.kind === "completed" ? null : (
        <div className="pb-actions">
          <button type="button" className="pb-btn" data-variant="primary" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      {stray ? (
        <div className="pb-actions">
          <button
            type="button"
            className="pb-btn"
            data-variant="quiet"
            onClick={() => navigate(`/session/${stray.session_id}`)}
          >
            {strayLabel}
          </button>
        </div>
      ) : null}
    </Shell>
  );
}

/**
 * `art` is optional because half the states this shell renders have no game to
 * show one for — a bad code, an expired link, the moment before the roster
 * lands. When there is a roster, the box is the strongest confirmation this
 * screen can give that the code went to the right table: this device has no
 * library of its own, so the art the server sent is the only art there is.
 */
function Shell(props: {
  title?: string;
  art?: ArtSubject;
  sub: string;
  children: ReactNode;
}): ReactNode {
  const head = (
    <header className={`pb-screen__head${props.art ? " pb-gamehead__head" : ""}`}>
      <h1 className="pb-screen__title">{props.title ?? "Punchboard"}</h1>
      <span className="pb-screen__sub">{props.sub}</span>
    </header>
  );

  return (
    <section className="pb-screen">
      {props.art ? (
        <div className="pb-gamehead">
          <BoxArt game={props.art} size={56} eager />
          {head}
        </div>
      ) : (
        head
      )}
      {props.children}
    </section>
  );
}

/** The roster's game, in the shape `BoxArt` reads. */
function rosterArt(roster: JoinRoster): ArtSubject {
  return {
    title: roster.game_title,
    thumb_url: roster.game_thumb_url,
    image_url: roster.game_image_url,
  };
}
